import React, { useCallback, useEffect, useState } from 'react';
import {
  Alert,
  AlertTitle,
  Box,
  Button,
  Chip,
  IconButton,
  Paper,
  Skeleton,
  Stack,
  Tooltip,
  Typography,
} from '@mui/material';
import RefreshIcon from '@mui/icons-material/Refresh';
import GppMaybeIcon from '@mui/icons-material/GppMaybe';
import bcraService from '../../../../services/bcraService';
import { getApiErrorMessage } from '../../../../utils/apiError';
import type { BcraCentralDeudoresConsulta } from '../../../../types/bcraCentralDeudores';
import {
  cuitConsultable,
  etiquetaNivelRiesgo,
  normalizarCuitConsulta,
} from '../../../../utils/bcraCentralDeudores';
import BcraCentralDeudoresDialog from '../../../../components/shared/BcraCentralDeudoresDialog';

interface Props {
  cuit?: string | null;
  onBcraData?: (data: BcraCentralDeudoresConsulta | null) => void;
  onLoadingChange?: (loading: boolean) => void;
}

const chipColor = (nivel: BcraCentralDeudoresConsulta['nivelRiesgo']) => {
  switch (nivel) {
    case 'riesgo':
      return 'error';
    case 'atencion':
      return 'warning';
    case 'normal':
      return 'success';
    default:
      return 'info';
  }
};

const Info360BcraStrip: React.FC<Props> = ({ cuit, onBcraData, onLoadingChange }) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [data, setData] = useState<BcraCentralDeudoresConsulta | null>(null);
  const [modalOpen, setModalOpen] = useState(false);

  const puedeConsultar = cuitConsultable(cuit);

  const consultar = useCallback(async () => {
    if (!puedeConsultar) {
      setData(null);
      setError(null);
      onBcraData?.(null);
      return;
    }

    setLoading(true);
    onLoadingChange?.(true);
    setError(null);
    try {
      const cuitNorm = normalizarCuitConsulta(cuit);
      const resultado = await bcraService.consultarCentralDeudores(cuitNorm);
      setData(resultado);
      onBcraData?.(resultado);
    } catch (err: unknown) {
      const msg = getApiErrorMessage(err, 'No se pudo consultar la Central de Deudores del BCRA.');
      setError(msg);
      setData(null);
      onBcraData?.(null);
    } finally {
      setLoading(false);
      onLoadingChange?.(false);
    }
  }, [cuit, onBcraData, onLoadingChange, puedeConsultar]);

  useEffect(() => {
    void consultar();
  }, [consultar]);

  if (!puedeConsultar) {
    return (
      <Alert severity="info" variant="outlined">
        Cliente sin CUIT — no es posible consultar la Central de Deudores del BCRA.
      </Alert>
    );
  }

  if (loading && !data) {
    return (
      <Paper variant="outlined" sx={{ p: 2, borderRadius: 2 }}>
        <Stack direction="row" spacing={1.5} alignItems="center">
          <GppMaybeIcon color="action" fontSize="small" />
          <Box sx={{ flex: 1 }}>
            <Skeleton width="40%" height={20} />
            <Skeleton width="70%" height={16} sx={{ mt: 0.5 }} />
          </Box>
        </Stack>
        <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
          Consultando Central de Deudores…
        </Typography>
      </Paper>
    );
  }

  if (error && !data) {
    return (
      <Alert
        severity="warning"
        variant="outlined"
        action={(
          <Button color="inherit" size="small" onClick={() => void consultar()} sx={{ textTransform: 'none' }}>
            Reintentar
          </Button>
        )}
      >
        {error}
      </Alert>
    );
  }

  if (!data) return null;

  return (
    <>
      <Paper variant="outlined" sx={{ p: 2, borderRadius: 2 }}>
        <Stack
          direction={{ xs: 'column', sm: 'row' }}
          spacing={1.5}
          alignItems={{ xs: 'flex-start', sm: 'center' }}
          justifyContent="space-between"
        >
          <Stack spacing={1} sx={{ flex: 1, minWidth: 0 }}>
            <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
              <GppMaybeIcon color="primary" fontSize="small" />
              <Typography variant="subtitle2" fontWeight={600}>
                Central de Deudores — BCRA
              </Typography>
              <Chip
                label={etiquetaNivelRiesgo(data.nivelRiesgo)}
                color={chipColor(data.nivelRiesgo)}
                size="small"
              />
              {data.consultaParcial ? (
                <Chip label="Consulta parcial" color="warning" size="small" variant="outlined" />
              ) : null}
            </Stack>
            <Typography variant="body2" color="text.secondary">
              {data.mensajeResumen}
            </Typography>
            <Stack direction="row" spacing={2} flexWrap="wrap" useFlexGap>
              {data.situacionMaxima != null ? (
                <Typography variant="caption" color="text.secondary">
                  Situación máxima: {data.situacionMaxima}
                </Typography>
              ) : null}
              {data.periodo ? (
                <Typography variant="caption" color="text.secondary">
                  Período: {data.periodo}
                </Typography>
              ) : null}
              <Typography variant="caption" color="text.secondary">
                Entidades: {data.entidades.length}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                Cheques BCRA: {data.chequesRechazados.length}
              </Typography>
            </Stack>
            {data.consultaParcial && data.errorConsulta ? (
              <Alert severity="warning" sx={{ py: 0 }}>
                <AlertTitle sx={{ fontSize: '0.8125rem', mb: 0 }}>Consulta parcial</AlertTitle>
                <Typography variant="caption">{data.errorConsulta}</Typography>
              </Alert>
            ) : null}
          </Stack>
          <Stack direction="row" spacing={0.5} alignItems="center" flexShrink={0}>
            <Tooltip title="Actualizar consulta BCRA">
              <span>
                <IconButton
                  size="small"
                  onClick={() => void consultar()}
                  disabled={loading}
                  aria-label="Actualizar consulta BCRA"
                >
                  <RefreshIcon fontSize="small" />
                </IconButton>
              </span>
            </Tooltip>
            <Button
              variant="outlined"
              size="small"
              onClick={() => setModalOpen(true)}
              sx={{ textTransform: 'none', whiteSpace: 'nowrap' }}
            >
              Ver informe completo
            </Button>
          </Stack>
        </Stack>
      </Paper>

      <BcraCentralDeudoresDialog
        open={modalOpen}
        onClose={() => setModalOpen(false)}
        data={data}
      />
    </>
  );
};

export default Info360BcraStrip;
